import type { BillingMode } from '../lib/types';
import { cn } from './ui';

/**
 * Reusable "what does one action cost?" table — same component on the public
 * marketing pricing page (so prospects see the cost structure before they sign
 * up) and inside the portal subscription page (so existing tenants can plan
 * their spend). Numbers come from `useBillingMode().creditCosts`, which the API
 * sources from `creditConfig.costs` — one super-admin tune in
 * /admin/settings/credits propagates to every surface.
 *
 * Three optional touches:
 *  - `referenceSaleCentsPer1000` → when non-zero, each row also shows
 *    "≈ €X / action". ADMIN-ONLY: on public pages and the tenant portal
 *    the euro cost per credit depends on which plan / credit pack the
 *    tenant buys, so a single "reference price" is misleading. Only the
 *    super-admin credit-config screen passes this — it's a margin-target
 *    preview for the operator, not a customer-facing rate.
 *  - `density` → "comfortable" on the marketing page, "compact" on the portal
 *    subscription page where space is at a premium.
 *  - `note` → optional caption rendered below the table (e.g. "Spamhaus-first
 *    means real blacklist COGS is typically near zero — the 15-credit weight
 *    only kicks in on the rare fallback path").
 */
/**
 * Table rows. Bot Anomaly Detection and Email Verification are merged: they
 * always run together on a single /v1/emails call and charge the sum of the
 * two configured credit weights. A `combine` row shows the summed credit
 * count (with a joined label) instead of two independent rows that would
 * imply the caller can pick one without the other — which the API doesn't
 * support anymore.
 */
interface CreditFeatureRow {
  key: keyof BillingMode['creditCosts'] | 'signupScreening';
  label: string;
  unit: string;
  /** For the merged row: which cost table entries to sum. */
  combine?: ReadonlyArray<keyof BillingMode['creditCosts']>;
}

const ROWS: ReadonlyArray<CreditFeatureRow> = [
  {
    key: 'signupScreening',
    label: 'Bot Anomaly Detection + Email Verification',
    unit: 'per signup screening — always coupled',
    combine: ['detection', 'emailVerification'],
  },
  { key: 'domainHealth', label: 'Domain Health', unit: 'per domain' },
  { key: 'blacklist', label: 'Blacklist check', unit: 'per domain' },
  { key: 'inboxPlacement', label: 'Inbox placement test', unit: 'per test' },
  { key: 'phoneValidation', label: 'Phone verification', unit: 'per lookup' },
];

function eur(cents: number): string {
  if (cents >= 100) return `€${(cents / 100).toFixed(2)}`;
  if (cents >= 1) return `${cents.toFixed(2)}c`;
  return `${cents.toFixed(3)}c`;
}

export function CreditCostTable({
  costs,
  referenceSaleCentsPer1000 = 0,
  density = 'comfortable',
  className,
  note,
}: {
  costs: BillingMode['creditCosts'];
  referenceSaleCentsPer1000?: number;
  density?: 'comfortable' | 'compact';
  className?: string;
  note?: string;
}) {
  const showMoney = referenceSaleCentsPer1000 > 0;
  const padY = density === 'compact' ? 'py-1.5' : 'py-3';
  const fontSize = density === 'compact' ? 'text-[12px]' : 'text-[13.5px]';

  // No min-width on the table itself — on a narrow phone (360-400px) a fixed
  // min-w forces horizontal scroll and clips the price column off the right
  // edge, which was the visible bug on /features/domain-health. Letting the
  // label cell wrap keeps all three columns visible at every viewport.
  return (
    <div className={cn(className)}>
      <table className={cn('w-full border-collapse', fontSize)}>
        <thead>
          <tr className="border-b border-bordersoft text-left text-[11px] uppercase tracking-wider text-faint">
            <th className={cn('px-3 font-medium', padY)}>Action</th>
            <th className={cn('px-3 text-right font-medium whitespace-nowrap', padY)}>Credits</th>
            {showMoney && <th className={cn('px-3 text-right font-medium whitespace-nowrap', padY)}>≈ Price</th>}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => {
            // Merged rows (like the signup-screening row) sum every constituent
            // credit weight. Plain rows read the single matching cost. Weights
            // resolve to 0 when the credit config hasn't been seeded yet (loading
            // window or wallet-off), matching the rest of the pricing UI.
            const credits = r.combine
              ? r.combine.reduce((sum, k) => sum + (costs[k] ?? 0), 0)
              : costs[r.key as keyof typeof costs] ?? 0;
            const cents = (credits / 1000) * referenceSaleCentsPer1000;
            return (
              <tr key={r.key} className="border-b border-bordersoft last:border-0">
                <td className={cn('px-3', padY)}>
                  <div className="font-medium text-txt">{r.label}</div>
                  <div className="font-mono text-[10.5px] text-faint">{r.unit}</div>
                </td>
                <td className={cn('px-3 text-right font-mono tabular-nums text-txt', padY)}>{credits}</td>
                {showMoney && (
                  <td className={cn('px-3 text-right font-mono tabular-nums text-muted', padY)}>{eur(cents)}</td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {note && <p className="mt-3 text-[11.5px] text-faint">{note}</p>}
    </div>
  );
}
